import { useCallback, useEffect, useState } from 'react';

import { useCart } from '@/Application/Selectors/cart/useCart';
import type { Cart } from '@/modules/cart/domain/Cart';
import { getGeneralShippingMethods } from '@/modules/shippingMethod/application/getGeneralShippingMethods';
import type { ShippingMethod } from '@/modules/shippingMethod/domain/ShippingMethod';
import type { ShippingMethodLineItem } from '@/Pages/ShippingPage/ShippingPageContext';
import { useShippingPageContext } from '@/Pages/ShippingPage/ShippingPageContext';

import { shippingMethodDtoToShippingMethod } from '../mappers/shippingMethodMapper';

import { useRepository } from './../../../Application/Providers/Repository/Repository';
import useShippingMethods from './useShippingMethods';

const sanitizeParams = (
  params: ShippingMethodLineItem[],
  cartSkus: string[],
  removeSkuFromHistory: (sku: string) => ShippingMethodLineItem[]
): ShippingMethodLineItem[] => {
  let updatedParams = params;

  const paramsSkus = updatedParams.map((p) => p.sku);

  // SKUs sobrantes, están en params pero no en cart
  const extraSkus = paramsSkus.filter((sku) => !cartSkus.includes(sku));

  // SKUs faltantes, están en cart pero no en params
  const missingSkus = cartSkus.filter((sku) => !paramsSkus.includes(sku));

  // Elimina SKUs sobrantes
  for (const sku of extraSkus) {
    updatedParams = removeSkuFromHistory(sku);
  }

  // Agrega SKUs faltantes con valores por defecto
  const newItems = missingSkus.map((sku) => ({
    sku,
    clientPreference: []
  }));

  // merge final
  updatedParams = [...updatedParams, ...newItems];
  return updatedParams;
};

const useGetGeneralShippingMethods = () => {
  const { shippingMethodsRepository } = useRepository();
  const { data: cart } = useCart();
  const { data: results, isLoading, error } = useShippingMethods();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { completeSkuHistory, getSkuHistoryUpdated, removeSkuFromHistory } =
    useShippingPageContext();
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod | null>(
    results && cart
      ? shippingMethodDtoToShippingMethod(results, cart, completeSkuHistory)
      : null
  );
  const [isError, setIsError] = useState<boolean>(Boolean(error));

  // JD => Esta función sanitiza los parámetros para que coincidan con los SKUs del carrito
  // seria correwcto integrarla en ShippingPageContext?

  const getShippingMethods = useCallback(
    async (
      params?: ShippingMethodLineItem[],
      updatedCart?: Cart | undefined
    ) => {
      if (!params) {
        params = await getSkuHistoryUpdated();
      }

      let cartSkus: string[] = [];

      if (updatedCart) {
        cartSkus =
          updatedCart?.lineItems
            ?.map((li) => li.variant?.sku)
            .filter(Boolean) ?? [];
      } else {
        cartSkus =
          cart?.lineItems?.map((li) => li.variant?.sku).filter(Boolean) ?? [];
      }

      // === SANITIZAR PARAMS ===
      const sanitizedParams = sanitizeParams(
        params,
        cartSkus,
        removeSkuFromHistory
      );

      const newShippingMethods = await getGeneralShippingMethods(
        shippingMethodsRepository,
        sanitizedParams.length > 0 ? sanitizedParams : completeSkuHistory
      );
      if (newShippingMethods) {
        setIsError(false);
      }
      return newShippingMethods;
    },
    [
      removeSkuFromHistory,
      shippingMethodsRepository,
      completeSkuHistory,
      getSkuHistoryUpdated,
      cart?.lineItems
    ]
  );

  useEffect(() => {
    if (results && cart && !isInitialLoad) {
      setShippingMethods(
        shippingMethodDtoToShippingMethod(results, cart, completeSkuHistory)
      );
    }
    if (isInitialLoad && results && cart) {
      setShippingMethods(
        shippingMethodDtoToShippingMethod(results, cart, completeSkuHistory)
      );
    }

    if (isInitialLoad && shippingMethods?.deliveryGroups.length) {
      setIsInitialLoad(false);
    }
  }, [
    results,
    cart,
    isInitialLoad,
    shippingMethods?.deliveryGroups?.length,
    completeSkuHistory
  ]);

  useEffect(() => {
    if (error) {
      setIsError(true);
    }
  }, [error]);

  return {
    results,
    unavailable: results?.unavailable,
    refetchShippingMethods: getShippingMethods,
    shippingMethods,
    isLoading,
    isError,
    error
  };
};

export default useGetGeneralShippingMethods;
